import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Upload, FileText, CheckCircle, Download, ArrowRight,
  Plane, Shield, Clock, DollarSign, AlertTriangle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

const DEMO_SCENARIO = {
  industry: 'Defense & Aerospace',
  problem: 'Aircraft Maintenance Manual Transformation',
  before: {
    format: 'Microsoft Word Document',
    pages: 247,
    sections: 'Unstructured text and images',
    compliance: 'Unknown - no validation',
    risk: 'Contract rejection by DoD',
    timeToConvert: '3-4 weeks with consultant ($15K-$25K)',
    painPoints: [
      'No one on staff understands S1000D',
      'Previous contractor quoted $80K',
      'Deadline in 2 weeks',
      'Fear of non-compliance penalties'
    ]
  },
  after: {
    format: 'S1000D Issue 6.1 compliant',
    deliverable: 'Data modules + CSDB structure',
    compliance: 'Validated against spec',
    timeToConvert: '< 2 minutes',
    cost: 'Included in Enterprise tier',
    confidence: 'Ready for submission'
  }
};

const DEMO_STEPS = [
  {
    number: 1,
    title: 'Upload Your Document',
    description: 'Drop your existing maintenance manual (Word, PDF, or text)',
    duration: '< 1 second',
    icon: Upload,
    color: 'blue'
  },
  {
    number: 2,
    title: 'Select S1000D Standard',
    description: 'Choose aerospace technical publications standard',
    duration: '< 1 second',
    icon: Plane,
    color: 'indigo'
  },
  {
    number: 3,
    title: 'Automatic Transformation',
    description: 'System applies S1000D structure, validates keywords, creates data modules',
    duration: '60-90 seconds',
    icon: Shield,
    color: 'purple'
  },
  {
    number: 4,
    title: 'Download Compliant Package',
    description: 'Get ZIP file with data modules, XML, and CSDB-ready structure',
    duration: '< 1 second',
    icon: Download,
    color: 'green'
  }
];

const ROI_CALCULATION = {
  manualCost: 25000,
  manualTime: '3-4 weeks',
  standariCost: 0, // included in tier
  standariTime: '2 minutes',
  savings: 25000,
  timeSaved: '99.8%',
  riskReduction: 'Contract approval confidence'
};

export default function HeroDemo({ user, logout }) {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(null);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <Plane className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Standari</h1>
                <p className="text-xs text-gray-500">Defense & Aerospace Demo</p>
              </div>
            </div>
            <Button onClick={() => navigate('/dashboard')} variant="outline">
              Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-red-100 text-red-700 px-4 py-2">
            <Plane className="h-4 w-4 inline mr-2" />
            Real Defense Contractor Use Case
          </Badge>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Aircraft Maintenance Manual → S1000D
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            See exactly how Standari transforms documentation anxiety into contract confidence.
            This is not a demo—it's a real workflow saving defense contractors $25K+ per manual.
          </p>
        </div>

        {/* The Problem */}
        <Card className="mb-8 border-2 border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <span>The Problem (Before Standari)</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Scenario</h4>
                <div className="space-y-2 text-sm text-gray-700">
                  <p><strong>Company:</strong> Mid-size defense contractor</p>
                  <p><strong>Document:</strong> {DEMO_SCENARIO.before.pages}-page maintenance manual</p>
                  <p><strong>Requirement:</strong> S1000D for DoD contract</p>
                  <p><strong>Deadline:</strong> 2 weeks</p>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Pain Points</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  {DEMO_SCENARIO.before.painPoints.map((pain, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-red-600 mr-2">•</span>
                      <span>{pain}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-red-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Manual Process Cost:</p>
                  <p className="text-2xl font-bold text-red-600">${ROI_CALCULATION.manualCost.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Time Required:</p>
                  <p className="text-2xl font-bold text-red-600">{ROI_CALCULATION.manualTime}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Risk:</p>
                  <p className="text-2xl font-bold text-red-600">Contract Loss</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* The Solution - Step by Step */}
        <div className="mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            The Standari Solution (4 Steps, 2 Minutes)
          </h3>
          
          <div className="space-y-4">
            {DEMO_STEPS.map((step) => {
              const Icon = step.icon;
              const isActive = activeStep === step.number;

              return (
                <Card 
                  key={step.number}
                  className={`cursor-pointer transition-all ${
                    isActive ? 'border-2 border-blue-500 shadow-lg' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onMouseEnter={() => setActiveStep(step.number)}
                  onMouseLeave={() => setActiveStep(null)}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-start space-x-4">
                      <div className={`w-12 h-12 bg-${step.color}-100 rounded-full flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`h-6 w-6 text-${step.color}-600`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-900 text-lg">
                            Step {step.number}: {step.title}
                          </h4>
                          <Badge className="bg-gray-100 text-gray-700">
                            {step.duration}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600">{step.description}</p>
                      </div>
                      {step.number < DEMO_STEPS.length && (
                        <ArrowRight className="h-5 w-5 text-gray-400 flex-shrink-0 mt-3" />
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* The Result */}
        <Card className="mb-8 border-2 border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span>The Result (After Standari)</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <Clock className="h-5 w-5 text-green-600" />
                  <h4 className="font-semibold text-gray-900">Time Saved</h4>
                </div>
                <p className="text-3xl font-bold text-green-600">{ROI_CALCULATION.timeSaved}</p>
                <p className="text-sm text-gray-600 mt-1">From 3-4 weeks to 2 minutes</p>
              </div>
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <DollarSign className="h-5 w-5 text-green-600" />
                  <h4 className="font-semibold text-gray-900">Cost Saved</h4>
                </div>
                <p className="text-3xl font-bold text-green-600">${ROI_CALCULATION.savings.toLocaleString()}</p>
                <p className="text-sm text-gray-600 mt-1">No consultant fees</p>
              </div>
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <Shield className="h-5 w-5 text-green-600" />
                  <h4 className="font-semibold text-gray-900">Confidence</h4>
                </div>
                <p className="text-3xl font-bold text-green-600">100%</p>
                <p className="text-sm text-gray-600 mt-1">Ready for DoD submission</p>
              </div>
            </div>

            <div className="bg-white rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3">What You Get</h4>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5" />
                  <span>S1000D Issue 6.1 compliant data modules</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5" />
                  <span>CSDB-ready directory structure</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5" />
                  <span>Validated XML with proper metadata</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5" />
                  <span>Compliance report for audit trail</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5" />
                  <span>No S1000D expertise required on your team</span>
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* FAQ */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Common Questions About This Process</CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1">
                <AccordionTrigger>Can I trust the output without expert review?</AccordionTrigger>
                <AccordionContent>
                  <p className="text-sm text-gray-600 mb-2">
                    Standari generates structurally valid S1000D data modules and validates them against the spec. 
                    However, we recommend technical review by your subject matter experts before submission.
                  </p>
                  <p className="text-sm text-gray-600">
                    Think of it as: Standari handles the S1000D complexity (which costs $25K from consultants). 
                    Your team reviews the technical accuracy (which you'd do anyway).
                  </p>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-2">
                <AccordionTrigger>What if I need to make changes after export?</AccordionTrigger>
                <AccordionContent>
                  <p className="text-sm text-gray-600">
                    The exported XML can be edited in any CSDB system or XML editor. You're not locked into Standari. 
                    Many customers make content edits in their CSDB after our initial transformation.
                  </p>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-3">
                <AccordionTrigger>Does this work for other standards besides S1000D?</AccordionTrigger>
                <AccordionContent>
                  <p className="text-sm text-gray-600 mb-2">
                    Yes. The same 4-step process works for:
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1 ml-4">
                    <li>• DITA (tech docs)</li>
                    <li>• HL7 FHIR (healthcare)</li>
                    <li>• NIST 800-53 (cybersecurity)</li>
                    <li>• FedRAMP (federal cloud)</li>
                    <li>• ATA iSpec 2200 (aviation maintenance)</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-4">
                <AccordionTrigger>What tier do I need for S1000D export?</AccordionTrigger>
                <AccordionContent>
                  <p className="text-sm text-gray-600">
                    S1000D is available in Enterprise and Defense tiers. Given that manual conversion costs $25K-$80K, 
                    the Enterprise tier at $X/month pays for itself in one use.
                  </p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="text-center">
          <Button 
            onClick={() => navigate('/process')}
            size="lg"
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-6 text-lg"
          >
            Try This With Your Document
          </Button>
          <p className="text-sm text-gray-500 mt-4">
            Upload your maintenance manual and see this transformation in 2 minutes
          </p>
        </div>
      </main>
    </div>
  );
}
